import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { mapPlaylistFromVideoSearch, Button, Dropdown, LoadingSpinner } from 'yankees-react-utils';
import { useOktaQueries, Player, PlayerReport } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import VideoPlayer from '../../../components/VideoPlayer';
import ReceivingChart from '../../../components/ReceivingChart';
import ReceivingRollingAvg from '../../../components/ReceivingRollingAvg';
import ReceivingTable from './ReceivingTable';
import { store } from '../../../store';

const StyledVideoContainer = styled.div`
    & card-body {
        margin: 0;
    }
`;
const StyledFiltersContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    > div {
        margin: 5px;
        margin-left: auto;
    }
`;
const StyledReportCard = styled(ReportCard)`
    margin-top: 10px;
`;

const buildQueries = (playerid, gameid, gameinfo, showall) => {
    const { gamedate, levelid } = gameinfo;
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['receivingReport', playerid, gameid],
            queryFn: (t) => PlayerReport.recv(t, {
                playerid,
                startdate: moment(gamedate).format('YYYY-MM-DD'),
                enddate: moment(gamedate).format('YYYY-MM-DD'),
                showall,
            }),
        },
        {
            queryKey: ['receivingReportYear', playerid, gameid],
            queryFn: (t) => PlayerReport.recv(t, {
                playerid,
                yearid: moment(gamedate).year(),
                showall: false,
                levelid,
            }),
        },
    ];
    return queries;
};

const mapDataResponse = (translate, response) => {
    const player = response[0].data || {};
    const recData = response[1].data || {};
    const headToHeadData = recData.total?.find((t) => [t.ready, t.relaxed, t.fb, t.runners_on, t.bases_empty, t.islhb].every((x) => x === null)) || {};
    const { rollingavgpitches, rollingavgh2h } = response[2].data || {};
    const rollingAvgPitches = rollingavgpitches.map((d) => ({ gamedate: d.gamedate, value: d.ravg_framingstk, split: 'overall' }));
    const rollingAvgH2HData = rollingavgh2h.map((d) => ({ gamedate: d.gamedate, value: d.h2h, split: 'h2h' }));
    const rollingAvgPitchData = rollingAvgPitches.concat(rollingAvgH2HData);
    const pitcherOptions = [{ name: translate('sections.post-game.receiving.all-pitchers'), filterFn: null }];
    const pitchers = recData.pitchlog?.map((p, index) => ({ pitcherid: p.pitcherid, pitcher: p.pitcher, sortorder: index })) || [];
    const uniquePitcherIds = [...new Set(pitchers?.map((p) => p.pitcherid))];
    uniquePitcherIds
        .map((id) => pitchers.find((p) => p.pitcherid === id)).sort((a, b) => a.sortorder - b.sortorder)
        .forEach((p) => pitcherOptions.push({ name: p.pitcher, filterFn: (x) => x.pitcherid === p.pitcherid }));
    return { player, recData, headToHeadData, rollingAvgPitchData, pitcherOptions };
};

const filterPitches = (pitchLog, passedBalls, filters) => {
    const { pitches, pitcher, pitchtype } = filters;
    const filteredPitches = pitchLog.filter((p) => (!pitches.filterFn || pitches.filterFn(p))
        && (!pitcher.filterFn || pitcher.filterFn(p)) && (!pitchtype.filterFn || pitchtype.filterFn(p)));
    const filteredPassedBalls = passedBalls.filter((p) => (!pitcher.filterFn || pitcher.filterFn(p))
        && (!pitchtype.filterFn || pitchtype.filterFn(p)));
    return { filteredPitches, filteredPassedBalls };
};

const getCatcherVideo = (pitchLog = [], user) => {
    const playlist = mapPlaylistFromVideoSearch({ videos: pitchLog, playervideos: [] }, user.Roles.map((r) => r.RoleName));
    return playlist;
};

const getPitchVideo = (pitchLog = [], pitchInfo = {}, user) => {
    const { gameid, pitchseq, eventseq } = pitchInfo;
    const video = pitchLog.find((v) => v.gameid === gameid && v.pitchseq === pitchseq && v.eventseq === eventseq);
    return mapPlaylistFromVideoSearch({ videos: [video], playervideos: [] }, user.Roles.map((r) => r.RoleName));
};

const pitchOptions = (translate) => [
    { name: translate('sections.post-game.receiving.all-results'), filterFn: null },
    { name: translate('baseball.strikes_other'), filterFn: (x) => x.isstrike === true },
    { name: translate('baseball.balls_other'), filterFn: (x) => x.isstrike === false },
    { name: translate('sections.post-game.receiving.gained'), filterFn: (x) => x.gained === true },
    { name: translate('sections.post-game.receiving.lost'), filterFn: (x) => x.lost === true },
];

const pitchTypeOptions = (translate) => [
    { name: translate('baseball.pitches.all-pitch-types'), filterFn: null },
    { name: translate('baseball.pitches.fastball_one'), filterFn: (x) => x.type === 1 },
    { name: translate('baseball.pitches.curveball'), filterFn: (x) => x.type === 2 },
    { name: translate('baseball.pitches.slider'), filterFn: (x) => x.type === 3 },
    { name: translate('baseball.pitches.changeup_one'), filterFn: (x) => x.type === 4 },
    { name: translate('baseball.pitches.sinker'), filterFn: (x) => x.type === 5 },
    { name: translate('baseball.pitches.knuckleball'), filterFn: (x) => x.type === 6 },
    { name: translate('baseball.pitches.splitter'), filterFn: (x) => x.type === 7 },
    { name: translate('baseball.pitches.cutter'), filterFn: (x) => x.type === 8 },
];

const Receiving = ({ playerid, gameid, gameinfo, showall = true }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const [playlist, setPlaylist] = useState([]);
    const [filters, setFilters] = useState({ pitches: pitchOptions(translate)[0], pitcher: { name: translate('sections.post-game.receiving.all-pitchers'), filterFn: null }, pitchtype: pitchTypeOptions(translate)[0] });
    const dataQueries = buildQueries(playerid, gameid, gameinfo, showall);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { player, recData, headToHeadData, rollingAvgPitchData, pitcherOptions } = mapDataResponse(translate, dataQueriesResponse);
    const { pitchlog, passedballs } = recData;
    const { filteredPitches, filteredPassedBalls } = filterPitches(pitchlog, passedballs, filters);

    const allVideosPlaylist = getCatcherVideo(filteredPitches, user);

    return (
        <>
            <Row>
                <Col xl={{ span: 4, order: 1 }} md={{ span: 12, order: 2 }}>
                    <StyledReportCard>
                        <ReceivingChart
                            pitches={filteredPitches}
                            passedBalls={filteredPassedBalls}
                            isGameData
                            onClick={(pitchInfo) => setPlaylist(getPitchVideo(pitchlog, pitchInfo, user))}
                        />
                        <StyledFiltersContainer>
                            <Dropdown
                                options={pitchOptions(translate)}
                                value={filters.pitches}
                                onSelect={(value) => setFilters({ ...filters, pitches: value })}
                            />
                            <Dropdown
                                options={pitcherOptions}
                                value={filters.pitcher}
                                onSelect={(value) => setFilters({ ...filters, pitcher: value })}
                            />
                            <Dropdown
                                options={pitchTypeOptions(translate)}
                                value={filters.pitchtype}
                                onSelect={(value) => setFilters({ ...filters, pitchtype: value })}
                            />
                            <div><Button variant="primary" onClick={() => setPlaylist(allVideosPlaylist)}>{translate('sections.post-game.receiving.play-videos')}</Button></div>
                        </StyledFiltersContainer>
                    </StyledReportCard>
                    <StyledReportCard>
                        <ReceivingTable headToHeadData={headToHeadData} player={player} />
                    </StyledReportCard>
                </Col>
                <Col xl={{ span: 8, order: 2 }} md={{ span: 12, order: 1 }}>
                    <StyledReportCard body={!playlist?.length}>
                        <StyledVideoContainer>
                            {playlist?.length ? <VideoPlayer playlist={playlist} maxHeight={640} /> : <h4>{translate('sections.post-game.receiving.no-videos-selected')}</h4>}
                        </StyledVideoContainer>
                    </StyledReportCard>
                </Col>
                <Col sm={{ span: 12, order: 3 }}>
                    <StyledReportCard title={`${player.LastName} ${translate('sections.post-game.receiving.extra-strikes-per-game-title')}`}>
                        <ReceivingRollingAvg rollingAvgData={rollingAvgPitchData} minY={-2.5} maxY={2.5} />
                    </StyledReportCard>
                </Col>
            </Row>
        </>
    );
};

Receiving.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    gameinfo: PropTypes.shape({
        gameid: PropTypes.number,
        gamedate: PropTypes.string,
        levelid: PropTypes.number,
    }).isRequired,
    showall: PropTypes.bool,
};

Receiving.defaultProps = {
    showall: true,
};

export default Receiving;
